package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.Role
import dev.lingvo.shared.isBillable
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.WorkspaceSelection
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Who is billable, and what each one costs.
 *
 * **An admin can already raise the bill by promoting somebody, so an admin has to be able to see the
 * price.** Hiding it behind the owner role does not protect anything — the lever is already in their
 * hands — it only means the person pulling it cannot see what it does.
 *
 * Held and billed are shown as two numbers because the interesting fact is whether they agree.
 * They disagree for a few seconds after every legitimate change, so a fresh mismatch reads as
 * "updating" rather than as an error: a warning that is usually wrong is one nobody reads when it is
 * finally right.
 */
@Page("/app/settings/billing/seats")
@Layout(".components.layouts.AppLayout")
@Composable
fun SeatsPage() {
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    var refreshKey by remember { mutableStateOf(0) }

    val billing = loadAsync(slug, refreshKey) { slug?.let { ApiClient.workspaceBilling(it) } }
    val members = loadAsync(slug, refreshKey) { slug?.let { ApiClient.workspaceMembers(it) } }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.settings.billing.seats.seats", "Seats"), t("app.settings.billing.seats.owners_and_admins_are_billable_translators", "Owners and admins are billable. Translators and viewers are free."))

        LAsyncContent(
            billing,
            rows = 3,
            loadingLabel = t("app.settings.billing.seats.loading_seats", "Loading seats"),
            onRetry = { refreshKey++ },
            gap = Space.XL,
        ) { overview ->
            if (overview == null) return@LAsyncContent
            val seats = overview.seats
            val price = overview.listPricePerSeat

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    Span(Modifier.tabularNumbers().fontWeight(600).fontSize(TypeScale.HeadingSmall).toAttrs()) {
                        Text(
                            tPlural(
                                "app.settings.billing.seats.count.billable_seat",
                                seats.held,
                                "{count} billable seat",
                                "{count} billable seats",
                            ),
                        )
                    }
                    // The number somebody came here for, said as a sentence rather than as a
                    // table they have to multiply themselves.
                    LMuted(
                        tf(
                            "app.settings.billing.seats.price_per_seat_each_month",
                            "{price} per seat each month on {plan}, before tax.",
                            "price" to price.display,
                            "plan" to overview.plan.displayName,
                        ),
                    )
                    when {
                        seats.billed == null ->
                            LMuted(t("app.settings.billing.seats.nothing_is_being_charged_yet", "Nothing is being charged yet."))
                        seats.matches ->
                            LMuted(tf(
                                "app.settings.billing.seats.processor_billing_matches",
                                "Your payment processor is billing for {billed}. That matches.",
                                "billed" to seats.billed,
                            ))
                        // Fresh disagreement is the normal few seconds after a promotion.
                        seats.syncing ->
                            LMuted(t("app.settings.billing.seats.updating_a_seat_change_takes_a", "Updating — a seat change takes a few seconds to reach the processor."))
                        // `{held}` and `{billed}` rather than Kotlin interpolation: a template
                        // is invisible to the catalogue extractor, so the sentence that tells a
                        // customer they are being billed for seats they do not hold was the one
                        // sentence on this page no translator could ever see.
                        else -> LAlert(
                            t("app.settings.billing.seats.the_seat_count_does_not_match", "The seat count does not match what is being billed"),
                            tf(
                                "app.settings.billing.seats.you_hold_held_billable_seats_and",
                                "You hold {held} billable seats and the processor says {billed}. Nothing you do here can fix that, and you should not have to — tell us and we will reconcile it. You will not be charged for seats you do not hold.",
                                "held" to seats.held.toString(),
                                "billed" to seats.billed.toString(),
                            ),
                            variant = LAlertVariant.WARNING,
                        )
                    }
                    overview.plan.maxBillableSeats?.let { cap ->
                        LMuted(
                            tf(
                                "app.settings.billing.seats.the_plan_plan_allows_seats_promoting",
                                "The {plan} plan allows {seats}. Promoting anybody past that needs a bigger plan.",
                                "plan" to overview.plan.displayName,
                                "seats" to tPlural(
                                    "app.settings.billing.seats.count.billable_seat",
                                    cap,
                                    "{count} billable seat",
                                    "{count} billable seats",
                                ),
                            ),
                        )
                    }
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.billing.seats.who_holds_one", "Who holds one"))
                    LAsyncContent(
                        members,
                        rows = 3,
                        loadingLabel = t("app.settings.billing.seats.loading_members", "Loading members"),
                        onRetry = { refreshKey++ },
                        gap = Space.MD,
                    ) { roster ->
                        val everyone = roster?.members.orEmpty()
                        LTable(caption = t("app.settings.billing.seats.members_and_what_they_cost", "Members and what they cost")) {
                            LTableHead {
                                LTableRow {
                                    // Spelled out rather than looped over a `listOf`: a
                                    // literal inside a list is invisible to the catalogue
                                    // extractor, so these three headers sat untranslated
                                    // above a table of translated rows.
                                    LTableHeaderCell {
                                        Text(t("app.settings.billing.seats.member", "Member"))
                                    }
                                    LTableHeaderCell {
                                        Text(t("app.settings.billing.seats.role", "Role"))
                                    }
                                    LTableHeaderCell {
                                        Text(t("app.settings.billing.seats.costs", "Costs"))
                                    }
                                }
                            }
                            LTableBody {
                                // Billable first: the free half of the list is not what
                                // anybody opened this page to count.
                                everyone.sortedBy { it.role.ordinal }.forEach { member ->
                                    LTableRow {
                                        LTableHeaderCell(scope = LTableHeaderScope.ROW) { Text(member.name) }
                                        LTableCell { LBadge(member.role.name.lowercase()) }
                                        LTableCell {
                                            P(Modifier.tabularNumbers().margin(0.px).toAttrs()) {
                                                Text(
                                                    if (member.role.isBillable) {
                                                        price.display
                                                    } else {
                                                        t("app.settings.billing.seats.free", "free")
                                                    },
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        val free = everyone.count { !it.role.isBillable }
                        if (free > 0) {
                            LMuted(
                                tPlural(
                                    "app.settings.billing.seats.count.free_person",
                                    free,
                                    "{count} person costs nothing.",
                                    "{count} people cost nothing.",
                                ) + " " +
                                    t("app.settings.billing.seats.translators_and_viewers_are_always_free", "Translators and viewers are always free, on every plan — that is not a trial."),
                            )
                        }
                        // The lever, named beside the price it moves.
                        LMuted(
                            tf(
                                "app.settings.billing.seats.role_change_price",
                                "Changing somebody to {admin} or {owner} adds {price} a month. " +
                                    "Changing them back removes it at the next renewal.",
                                "admin" to Role.ADMIN.name.lowercase(),
                                "owner" to Role.OWNER.name.lowercase(),
                                "price" to price.display,
                            ),
                        )
                    }
                }
            }

            Row(Modifier.gap(Space.MD)) {
                Link(path = "/app/settings/members") { Text(t("app.settings.billing.seats.change_who_is_what", "Change who is what")) }
                Link(path = "/app/settings/billing") { Text(t("app.settings.billing.seats.billing", "Billing")) }
                Link(path = "/app/settings/billing/invoices") { Text(t("app.settings.billing.seats.invoices", "Invoices")) }
            }
        }
    }
}

